import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { isAtLeast } from '@/lib/roles';
import { defaultFeaturePermissions, FEATURE_DEFINITIONS, ROLE_KEYS, FeaturePermissionProvider, useFeaturePermissions } from '@/lib/featurePermissions';

export { useFeaturePermissions };
import { cloud } from '@/backend';
import { store } from '@/storage/sqliteStore';
import { getActiveTenantId } from '@/lib/tenantContext';

function organisationIdFor(user) {
  return user?.organisation_id || user?.organization_id || user?.org_id || 'default';
}

function normaliseMatrix(value) {
  const defaults = defaultFeaturePermissions();
  const source = value && typeof value === 'object' ? value : {};
  for (const feature of FEATURE_DEFINITIONS) {
    for (const role of ROLE_KEYS) {
      if (typeof source?.[feature.key]?.[role] === 'boolean') defaults[feature.key][role] = source[feature.key][role];
    }
  }
  return defaults;
}

export default function FeaturePermissionContextProvider({ children }) {
  const { user, effectiveRole, isAuthenticated } = useAuth();
  const [matrix, setMatrix] = useState(() => defaultFeaturePermissions());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organisationId, setOrganisationId] = useState(() => organisationIdFor(user));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const tenant = await getActiveTenantId();
        if (active && tenant) setOrganisationId(tenant);
      } catch { /* local identity is still a valid offline fallback */ }
    })();
    return () => { active = false; };
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setMatrix(defaultFeaturePermissions());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let rows = await base44.entities.FeaturePermission.filter({ organisation_id: organisationId });
      if (cloud.isEnabled()) {
        try {
          const remote = await cloud.pullOrganisationRecords(organisationId, ['FeaturePermission']);
          for (const record of remote) { if (!record.deleted && record.payload) await store('FeaturePermission').put(record.payload); }
          rows = await base44.entities.FeaturePermission.filter({ organisation_id: organisationId });
        } catch (remoteError) {
          console.warn('[DrillLogPro] organisation feature permissions pull skipped:', remoteError?.message || remoteError);
        }
      }
      const record = rows?.sort((a, b) => new Date(b.updated_date || 0) - new Date(a.updated_date || 0))?.[0];
      setMatrix(normaliseMatrix(record?.permissions));
    } catch (error) {
      console.warn('[DrillLogPro] feature permissions load skipped:', error?.message || error);
      setMatrix(defaultFeaturePermissions());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, organisationId]);

  useEffect(() => { void load(); }, [load]);

  const canUseFeature = useCallback((featureKey, roleOverride) => {
    const role = String(roleOverride || effectiveRole || user?.role || 'rig').toLowerCase();
    const feature = FEATURE_DEFINITIONS.find((item) => item.key === featureKey);
    if (!feature) return true;
    if (!isAtLeast(role, feature.minRole)) return false;
    // Admin/Owner retain access to the permission-management controls so they
    // can recover from an intentionally disabled feature without a lockout.
    if (feature.key === 'feature_permissions' && isAtLeast(role, 'admin')) return true;
    return matrix?.[featureKey]?.[role] !== false;
  }, [matrix, effectiveRole, user?.role]);

  const updateMatrix = useCallback(async (nextMatrix) => {
    if (!isAtLeast(user?.role, 'admin')) throw new Error('Admin or Owner access required.');
    const next = normaliseMatrix(nextMatrix);
    setSaving(true);
    try {
      const rows = await base44.entities.FeaturePermission.filter({ organisation_id: organisationId });
      const existing = rows?.[0];
      const payload = { organisation_id: organisationId, permissions: next, version: Number(existing?.version || 0) + 1 };
      if (existing) await base44.entities.FeaturePermission.update(existing.id, payload);
      else await base44.entities.FeaturePermission.create(payload);
      setMatrix(next);
      return next;
    } finally {
      setSaving(false);
    }
  }, [organisationId, user?.role]);

  const value = useMemo(() => ({ matrix, loading, saving, canUseFeature, updateMatrix, reload: load }), [matrix, loading, saving, canUseFeature, updateMatrix, load]);
  return <FeaturePermissionProvider value={value}>{children}</FeaturePermissionProvider>;
}
